import React, { useCallback, useRef, useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import {
    Column,
    DataGrid,
    Selection,
    Export,
    Sorting,
    Paging,
    Toolbar,
    Item,
    Editing,
    FilterRow,
    StateStoring,
    ColumnChooser,
    ColumnChooserSearch,
    Lookup
} from "devextreme-react/data-grid";
import DataSource from "devextreme/data/data_source";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import { LoadPanel } from 'devextreme-react/load-panel';
import { Button } from 'devextreme-react/button';
import { confirm } from 'devextreme/ui/dialog';
import data from "../../../../../assets/apiRoutes.json";
import notify from 'devextreme/ui/notify';
import { GridLayout } from "../../../../../common/GridLayout";
import GridLayoutManager from "../../../../../gridLayoutCustomization/GridLayoutManager";
import RenderSelectBoxCell from "../../../../../common/RenderCell";

const ALL_DAY = 'A';
const HOURS = 'H';

const exceptionDurationOptions = [
    { id: ALL_DAY, text: 'All Day' },
    { id: HOURS, text: 'Hours' },
];

const timeEditorOptions = { type: 'time' };

// User-defined references are varchar(50) in the DB; cap the grid editor to match.
const referenceEditorOptions = { maxLength: 50 };

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

function DetailsTab(props) {
    const accessToken = props?.oidc?.user?.access_token;
    const detailGrid = useRef(null);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const [selectedDetailKeys, setSelectedDetailKeys] = useState([]);
    const detailGridKey = "7A1C3E85-2B64-4F09-9D5A-6C8E1B4F7A32";
    const [detailLayout, setDetailLayout] = useState("Default");

    const position = { of: '#accordion' };

    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, []);

    const loadState = useCallback(() => {
        return GridLayout.sendStorageRequest(detailGridKey, "GET", accessToken, null, detailLayout);
    }, [detailLayout, accessToken]);

    const saveState = useCallback((state) => {
        GridLayout.sendStorageRequest(detailGridKey, "POST", accessToken, state, detailLayout);
    }, [detailGridKey, detailLayout, accessToken]);

    const dataSource = useMemo(() => {
        if (!props.headerId || props.headerId === EMPTY_GUID) {
            return new DataSource({
                store: [],
                key: "RowId"
            });
        }

        return new DataSource({
            store: createStore({
                key: "RowId",
                loadUrl: `${window._env_.REACT_APP_APIS}/${data.ExceptionDetails.GetExceptionDetails}`,
                insertUrl: `${window._env_.REACT_APP_APIS}/${data.ExceptionDetails.AddExceptionDetail}`,
                updateUrl: `${window._env_.REACT_APP_APIS}/${data.ExceptionDetails.EditExceptionDetail}`,
                deleteUrl: `${window._env_.REACT_APP_APIS}/${data.ExceptionDetails.DeleteExceptionDetail}`,
                loadParams: {
                    exceptionsHeaderRowId: props.headerId
                },
                // Only attach auth headers here. The request payload itself is shaped through the
                // grid's onRowInserting / onRowUpdating events, not by rewriting the request body.
                onBeforeSend: function (method, ajaxOptions) {
                    ajaxOptions.headers = {
                        Authorization: `Bearer ${accessToken}`,
                        access_token: accessToken,
                    };
                }
            }),
            key: "RowId",
            paginate: false
        });
    }, [props.headerId, accessToken]);


    const onSelectionChanged = useCallback((e) => {
        if (e.selectedRowKeys.length > 1) {
            const list = (e.currentSelectedRowKeys && e.currentSelectedRowKeys.length)
                ? e.currentSelectedRowKeys : e.selectedRowKeys;
            e.component.selectRows([list[list.length - 1]], false);
            return;
        }
        setSelectedDetailKeys(e.selectedRowKeys);
    }, []);

    const onInitNewRow = useCallback((e) => {
        e.data.RowId = EMPTY_GUID;
        e.data.RExceptionsheader = props.headerId;
        e.data.ExceptionDate = new Date();
        e.data.ExceptionDuration = ALL_DAY;
        e.data.DurationFrom = null;
        e.data.DurationTo = null;
        e.data.Comment = '';
    }, [props.headerId]);

    // All-Day rows carry no time range.
    const normalizeDuration = (row) => {
        if (row.ExceptionDuration !== HOURS) {
            row.DurationFrom = null;
            row.DurationTo = null;
        }
    };

    // Shape the insert payload here (not in onBeforeSend): stamp the parent header + a blank RowId
    // and clear the time range for All-Day rows. The grid sends e.data as the request "values".
    const onRowInserting = useCallback((e) => {
        e.data.RowId = EMPTY_GUID;
        e.data.RExceptionsheader = props.headerId;
        normalizeDuration(e.data);
    }, [props.headerId]);

    // Cell editing sends only the changed cell, so merge the existing row to persist the full
    // record, then shape the update payload (parent header + All-Day time clearing).
    const onRowUpdating = useCallback((e) => {
        e.newData = { ...e.oldData, ...e.newData };
        e.newData.RExceptionsheader = props.headerId;
        normalizeDuration(e.newData);
    }, [props.headerId]);

    // Success toasts live in the grid's own row events (they fire only after the store op resolves),
    // not in the createStore callbacks.
    const onRowInserted = useCallback(() => {
        notify("Exception Detail inserted successfully.", "success", 3000);
    }, []);

    const onRowUpdated = useCallback(() => {
        notify("Exception Detail updated successfully.", "success", 3000);
    }, []);

    // Exception Duration drives the time range. Cell editing commits the row as soon as the cell
    // loses focus, so switching to Hours with empty From/To would fail the "required" rule and
    // trap focus. To avoid that, default empty From/To to 12:00 AM when Hours is picked (the user
    // then adjusts them); switching back to All Day clears the (now hidden) range.
    // Use a local, no-"Z" string that matches the column serializationFormat (yyyy-MM-ddTHH:mm:ss);
    // a raw Date would be serialized to UTC and shift the time across timezones.
    const onDurationChanged = useCallback((newData, value, currentRowData) => {
        newData.ExceptionDuration = value;
        if (value === HOURS) {
            const now = new Date();
            const p = (n) => String(n).padStart(2, '0');
            const today = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
            if (!currentRowData?.DurationFrom) {
                newData.DurationFrom = `${today}T00:00:00`; // 12:00 AM
            }
            if (!currentRowData?.DurationTo) {
                newData.DurationTo = `${today}T01:00:00`; // 1:00 AM
            }
        } else {
            newData.DurationFrom = null;
            newData.DurationTo = null;
        }
    }, []);

    // Duration From/To are only editable when Exception Duration is Hours.
    const onEditorPreparing = useCallback((e) => {
        if (e.parentType !== "dataRow") return;
        if (e.dataField === "DurationFrom" || e.dataField === "DurationTo") {
            const duration = e.row?.data?.ExceptionDuration;
            e.editorOptions.disabled = duration !== HOURS;
        }
    }, []);

    // Confirm then delete the single selected row via its store.
    const deleteSelectedRow = useCallback(() => {
        const grid = detailGrid.current?.instance?.();
        if (!grid) return;
        const keys = grid.getSelectedRowKeys();
        if (!keys.length) return;
        const key = keys[0];
        confirm("Delete the selected record?", "Confirm delete").then((yes) => {
            if (!yes) return;
            const store = grid.getDataSource().store();
            // Delete runs via store.remove (not grid inline delete), so the grid's onRowRemoved
            // event never fires - surface the toasts here instead.
            store.remove(key).then(() => {
                notify("Exception Detail deleted successfully.", "success", 3000);
                grid.clearSelection();
                grid.refresh();
            }).catch((error) => {
                const message =
                    (typeof error === "string" ? error : null) ||
                    error?.message ||
                    error?.responseJSON?.message ||
                    "Failed to delete Exception Detail.";
                notify(message, "error", 5000);
            });
        });
    }, []);

    const headerSaved = props.headerId && props.headerId !== EMPTY_GUID;

    // User-defined code columns are ID-code lookups (id/Lookup/GetIDCodes filtered by code type),
    // rendered with the shared RenderSelectBoxCell in editCellRender. GetIDCodes returns { id, name },
    // so value/display is "name". The detail code types (4A-4E) intentionally mirror the header's.
    const codeEditCellRender = useCallback((field, codeType) => (cellElement) =>
        RenderSelectBoxCell({
            loadUrl: `${window._env_.REACT_APP_APIS}/${data.ID.GetIDCodes}`,
            loadParams: { codeType: function () { return codeType; } },
            key: "name",
            displayExpr: "name",
            valueExpr: "name",
            accessToken: accessToken,
            sort: [{ selector: "name", desc: false }],
            defaultValue: cellElement.data[field],
            onValueChangeCallback: (value, text) => {
                cellElement.setValue({ value, text });
            },
        }), [accessToken]);

    const setCodeCellValue = useCallback((field) => (rowData, newValue) => {
        rowData[field] = newValue ? newValue.text : null;
    }, []);

    return (
        <div>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                position={position}
                onHiding={hideLoadPanel}
                visible={loadPanelVisible}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
            />

            <div style={{ height: '72vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ flex: '1 1 auto', minHeight: 0 }}>
                    <DataGrid
                        dataSource={dataSource}
                        ref={detailGrid}
                        showBorders={true}
                        rowAlternationEnabled={true}
                        allowColumnReordering={true}
                        allowColumnResizing={true}
                        columnAutoWidth={true}
                        onSelectionChanged={onSelectionChanged}
                        onInitNewRow={onInitNewRow}
                        onRowInserting={onRowInserting}
                        onRowInserted={onRowInserted}
                        onRowUpdating={onRowUpdating}
                        onRowUpdated={onRowUpdated}
                        onEditorPreparing={onEditorPreparing}
                        height="100%"
                        noDataText={headerSaved ? "No exception details found" : "Save the header first to add exception details"}
                    >
                        <StateStoring
                            enabled={true}
                            type="custom"
                            customLoad={loadState}
                            storageKey={detailGridKey}
                            customSave={saveState}
                        />
                        <ColumnChooser
                            enabled={true}
                            title="Choose Column"
                            width={290}
                            height={290}
                            sortOrder="asc"
                        >
                            <ColumnChooserSearch
                                enabled={true}
                                editorOptions={{ placeholder: 'Search column' }} />
                        </ColumnChooser>
                        <Selection mode="multiple" showCheckBoxesMode="always" allowSelectAll={false} />
                        <Export enabled={true} allowExportSelectedData={true} />
                        <Editing
                            mode="cell"
                            refreshMode="repaint"
                            allowAdding={!!headerSaved}
                            allowUpdating={!!headerSaved}
                            allowDeleting={false}
                            newRowPosition="last"
                        />
                        <FilterRow visible={true} />
                        <Sorting mode="none" />
                        <Paging enabled={false} />

                        <Column
                            dataField="RecordNumber"
                            caption="Record #"
                            allowEditing={false}
                            allowSorting={false}
                        />
                        <Column
                            dataField="ExceptionDate"
                            caption="Exception Date"
                            dataType="date"
                            serializationFormat="yyyy-MM-ddTHH:mm:ss"
                            format="MM/dd/yyyy"
                            allowSorting={false}
                        />
                        <Column
                            dataField="ExceptionDuration"
                            caption="Exception Duration"
                            allowSorting={false}
                            setCellValue={onDurationChanged}
                        >
                            <Lookup
                                dataSource={exceptionDurationOptions}
                                valueExpr="id"
                                displayExpr="text"
                            />
                        </Column>
                        <Column
                            dataField="DurationFrom"
                            caption="Duration From"
                            dataType="datetime"
                            serializationFormat="yyyy-MM-ddTHH:mm:ss"
                            format="shortTime"
                            editorOptions={timeEditorOptions}
                            allowSorting={false}
                        />
                        <Column
                            dataField="DurationTo"
                            caption="Duration To"
                            dataType="datetime"
                            serializationFormat="yyyy-MM-ddTHH:mm:ss"
                            format="shortTime"
                            editorOptions={timeEditorOptions}
                            allowSorting={false}
                        />
                        <Column
                            dataField="Comment"
                            caption="Comment"
                            dataType="string"
                            allowSorting={false}
                        />

                        {/* User-defined codes (ID-code lookups) */}
                        <Column visible={false} dataField="UserDefinedCode1" caption="Code 1" dataType="string" allowSorting={false}
                            editCellRender={codeEditCellRender("UserDefinedCode1", "4A")} setCellValue={setCodeCellValue("UserDefinedCode1")} />
                        <Column visible={false} dataField="UserDefinedCode2" caption="Code 2" dataType="string" allowSorting={false}
                            editCellRender={codeEditCellRender("UserDefinedCode2", "4B")} setCellValue={setCodeCellValue("UserDefinedCode2")} />
                        <Column visible={false} dataField="UserDefinedCode3" caption="Code 3" dataType="string" allowSorting={false}
                            editCellRender={codeEditCellRender("UserDefinedCode3", "4C")} setCellValue={setCodeCellValue("UserDefinedCode3")} />
                        <Column visible={false} dataField="UserDefinedCode4" caption="Code 4" dataType="string" allowSorting={false}
                            editCellRender={codeEditCellRender("UserDefinedCode4", "4D")} setCellValue={setCodeCellValue("UserDefinedCode4")} />
                        <Column visible={false} dataField="UserDefinedCode5" caption="Code 5" dataType="string" allowSorting={false}
                            editCellRender={codeEditCellRender("UserDefinedCode5", "4E")} setCellValue={setCodeCellValue("UserDefinedCode5")} />

                        {/* User-defined dates (date only) */}
                        <Column visible={false} dataField="UserDefinedDate1" caption="Date 1" dataType="date" serializationFormat="yyyy-MM-ddTHH:mm:ss" format="MM/dd/yyyy" allowSorting={false} />
                        <Column visible={false} dataField="UserDefinedDate2" caption="Date 2" dataType="date" serializationFormat="yyyy-MM-ddTHH:mm:ss" format="MM/dd/yyyy" allowSorting={false} />
                        <Column visible={false} dataField="UserDefinedDate3" caption="Date 3" dataType="date" serializationFormat="yyyy-MM-ddTHH:mm:ss" format="MM/dd/yyyy" allowSorting={false} />
                        <Column visible={false} dataField="UserDefinedDate4" caption="Date 4" dataType="date" serializationFormat="yyyy-MM-ddTHH:mm:ss" format="MM/dd/yyyy" allowSorting={false} />
                        <Column visible={false} dataField="UserDefinedDate5" caption="Date 5" dataType="date" serializationFormat="yyyy-MM-ddTHH:mm:ss" format="MM/dd/yyyy" allowSorting={false} />

                        {/* User-defined quantities (2 decimals) */}
                        <Column visible={false} dataField="UserDefinedQuantity1" caption="Quantity 1" dataType="number" format="#0.00" allowSorting={false} />
                        <Column visible={false} dataField="UserDefinedQuantity2" caption="Quantity 2" dataType="number" format="#0.00" allowSorting={false} />
                        <Column visible={false} dataField="UserDefinedQuantity3" caption="Quantity 3" dataType="number" format="#0.00" allowSorting={false} />
                        <Column visible={false} dataField="UserDefinedQuantity4" caption="Quantity 4" dataType="number" format="#0.00" allowSorting={false} />
                        <Column visible={false} dataField="UserDefinedQuantity5" caption="Quantity 5" dataType="number" format="#0.00" allowSorting={false} />

                        {/* User-defined references (max 50 chars) */}
                        <Column visible={false} dataField="UserDefinedReference1" caption="Reference 1" dataType="string" allowSorting={false} editorOptions={referenceEditorOptions} />
                        <Column visible={false} dataField="UserDefinedReference2" caption="Reference 2" dataType="string" allowSorting={false} editorOptions={referenceEditorOptions} />
                        <Column visible={false} dataField="UserDefinedReference3" caption="Reference 3" dataType="string" allowSorting={false} editorOptions={referenceEditorOptions} />
                        <Column visible={false} dataField="UserDefinedReference4" caption="Reference 4" dataType="string" allowSorting={false} editorOptions={referenceEditorOptions} />
                        <Column visible={false} dataField="UserDefinedReference5" caption="Reference 5" dataType="string" allowSorting={false} editorOptions={referenceEditorOptions} />
                        <Toolbar>
                            <Item name="addRowButton" location="after" />
                            <Item location="after" locateInMenu="never">
                                <Button
                                    icon="trash"
                                    hint="Delete selected"
                                    stylingMode="text"
                                    disabled={selectedDetailKeys.length === 0}
                                    onClick={deleteSelectedRow}
                                />
                            </Item>
                            <Item location="after" locateInMenu="never">
                                <div className="grid-toolbar-sep" />
                            </Item>
                            <Item location="after" locateInMenu="never">
                                <GridLayoutManager gridKey={detailGridKey} dataGrid={detailGrid} setCurrentLayout={setDetailLayout} />
                            </Item>
                            <Item name="saveButton" />
                            <Item name="revertButton" />
                            <Item name="exportButton" />
                            <Item name="columnChooserButton" />
                        </Toolbar>
                    </DataGrid>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(DetailsTab);
